<script setup>
import { computed, onMounted, ref, useSlots } from 'vue';
import { PhxIcon, SimpleSlider } from '@phx-styleguides/jlc';
import PictureLazy from '@maison/components/common/PictureLazy.vue';
import WishlistButton from '@maison/components/wishlist/WishlistButton.vue';
import WatchComparatorButton from '@maison/components/watchComparator/WatchComparatorButton.vue';
import ThreeDImage from '@maison/components/product/ThreeDImage.vue';
import Tag from '@maison/components/atoms/Tag.vue';
import Fragment from '@phoenix/components/common/Fragment';
import { usePrice } from '@phoenix/composables/price';
import ProductPrice from '@phoenix/components/common/ProductPrice.vue';
import { get } from 'lodash-es';

const props = defineProps({
  orientation: {
    type: String,
    default: 'vertical',
    validator: (value) => ['vertical', 'horizontal'].includes(value),
  },
  size: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'small', 'large'].includes(value),
  },
  id: {
    type: [String, Number],
    default: '',
  },
  modelReference: {
    type: String,
    default: '',
  },
  sapReference: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  collection: {
    type: String,
    default: '',
  },
  url: {
    type: String,
    default: '',
  },
  image: {
    type: Object,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  ctaText: {
    type: String,
    default: '',
  },
  textLink: {
    type: String,
    default: '',
  },
  enableSlider: {
    type: Boolean,
    default: false,
  },
  sliderImages: {
    type: Array,
    default: () => [],
  },
  sliderHoverIndex: {
    type: Number,
    default: 1,
  },
  variants: {
    type: Array,
    default: () => [],
  },
  tracking: {
    type: Object,
    default: null,
  },
  has3D: {
    type: Boolean,
    default: false,
  },
  labels: {
    type: Array,
    default: () => [],
  },
  errors: {
    type: Array,
    default: () => [],
  },
  price: {
    type: [String, Object],
    default: null,
  },
  vatInformation: {
    type: String,
    default: '',
  },
  shouldDisplayVatInformation: {
    type: Boolean,
    default: false,
  },
  shouldDisplayFromPrice: {
    type: Boolean,
    default: false,
  },
  shouldDisplayAddToWishlist: {
    type: Boolean,
    default: true,
  },
  shouldDisplayAddToWatchComparator: {
    type: Boolean,
    default: false,
  },
  shouldDisplaySapReference: {
    type: Boolean,
    default: false,
  },
  shouldDisplayVariants: {
    type: Boolean,
    default: true,
  },
  inWishlistIcon: {
    type: String,
    default: undefined,
  },
  dataTracking: {
    type: Object,
    default: null,
  },
  dataCy: {
    type: String,
    default: 'product-card',
  },
  dataCyRemoveButton: {
    type: String,
    default: 'product-card-remove-button',
  },
  dataCyWishlistButton: {
    type: String,
    default: undefined,
  },
  openLinkInNewWindow: {
    type: Boolean,
    default: false,
  },
  displayClasses: {
    type: Array,
    default: () => [],
  },
  linkClasses: {
    type: Array,
    default: () => [],
  },
  onClick: {
    type: Function,
    default: null,
  },
  onRemove: {
    type: Function,
    default: null,
  },
  headingTag: {
    type: String,
    default: 'h5',
    validator: (value) => ['h5', 'div'].includes(value),
  },
});

const slots = useSlots();
const { localizedPrice } = usePrice();

const linkRef = ref(null);

const sliderRef = ref(null);
const sliderPaginationRef = ref(null);
const sliderPrevRef = ref(null);
const sliderNextRef = ref(null);
const _vatInformation = computed(() =>
  props.vatInformation === '' && typeof props.price === 'object'
    ? get(localizedPrice(props.price), 'vatInformation', '')
    : props.vatInformation,
);
const shouldDisplayRemoveButton = computed(() => props.onRemove !== null);
const hasSliderImages = computed(() => props.enableSlider && props.sliderImages.length > 1);
const hasVariants = computed(() => props.shouldDisplayVariants && props.variants.length > 0);
const hasVariantsIndicator = computed(() => props.variants.length > 3);
const hasActions = computed(() => props.shouldDisplayAddToWishlist || shouldDisplayRemoveButton.value);
const hasCta = computed(() => props.ctaText !== '');
const hasCollection = computed(() => props.collection !== '');
const hasName = computed(() => props.name !== '');
const hasTextLink = computed(() => props.textLink !== '');
const hasLabels = computed(() => props.labels.length > 0);
const hasSapReference = computed(() => props.shouldDisplaySapReference && props.sapReference !== '');
const hasDescription = computed(() => props.description !== '' || !!slots.description);
const hasSpecs = computed(() => hasSapReference.value || hasDescription.value);
const hasPrice = computed(() => props.price !== null);
const hasVatInformation = computed(() => props.shouldDisplayVatInformation && _vatInformation.value !== '');
const cardClasses = computed(() => [
  'product-card',
  {
    'product-card-with-slider': hasSliderImages.value,
    'product-card--small-vertical': props.orientation === 'vertical' && props.size === 'small',
    'product-card--horizontal': props.orientation === 'horizontal',
    'product-card--small': props.orientation === 'horizontal' && props.size === 'small',
    'product-card--large': props.orientation === 'horizontal' && props.size === 'large',
    'product-card--with-quick-cta': hasCta.value,
    'product-card--selectable': props.shouldDisplayAddToWatchComparator,
  },
  ...props.displayClasses,
]);
const linkTag = computed(() => {
  if (props.url !== '') {
    return 'a';
  }

  if (props.onClick !== null) {
    return 'button';
  }

  return Fragment;
});

const onLinkClick = (event) => {
  if (props.onClick) {
    props.onClick(event);
  }
};

const linkAttrs = computed(() => {
  const commonAttrs = {
    class: ['product-card__link', 'expand-target', ...props.linkClasses],
    'data-tracking': props.dataTracking ? JSON.stringify(props.dataTracking) : null,
  };

  if (linkTag.value === 'button') {
    return {
      ...commonAttrs,
      type: 'button',
      onClick: onLinkClick,
    };
  }

  if (linkTag.value === 'a') {
    return {
      ...commonAttrs,
      href: props.url,
      target: props.openLinkInNewWindow ? '_blank' : '_self',
      onClick: onLinkClick,
    };
  }

  return {};
});

const onPictureClick = () => {
  if (!props.url) {
    return;
  }

  linkRef?.value?.click();
};

onMounted(() => {
  if (hasSliderImages.value && sliderRef.value) {
    new SimpleSlider(sliderRef.value, {
      navigation: {
        prev: sliderPrevRef.value,
        next: sliderNextRef.value,
      },
      pagination: sliderPaginationRef.value,
      showOnHoverIndex:
        props.sliderHoverIndex >= props.sliderImages.length ? props.sliderImages.length - 1 : props.sliderHoverIndex,
    });
  }
});
</script>

<template>
  <div :class="cardClasses" :data-cy="dataCy" :data-cy-reference="modelReference">
    <div v-if="hasSliderImages" ref="sliderRef" class="simple-slider product-card__slider">
      <div class="simple-slider__body">
        <div
          v-for="sliderImage in sliderImages"
          :key="sliderImage.image.src"
          class="simple-slider__slide product-card__picture"
        >
          <div class="picture" @click="onPictureClick">
            <picture-lazy v-bind="sliderImage" />
          </div>
        </div>
      </div>

      <div ref="sliderPaginationRef" class="product-card__slider-pagination"></div>
    </div>

    <div v-else class="product-card__picture">
      <div class="picture">
        <picture-lazy v-bind="image" />
      </div>
    </div>

    <div class="product-card__body">
      <component :is="linkTag" v-bind="linkAttrs" ref="linkRef">
        <div class="product-card__details">
          <ul v-if="hasLabels" class="tags-list product-card__tags" data-cy="product-tags">
            <li
              v-for="(label, index) in labels"
              :key="index"
              :class="['tags-list__item', { 'max-sm:hidden': index !== 0 }]"
            >
              <tag v-bind="label" />
            </li>
          </ul>

          <component :is="headingTag" v-if="hasCollection" class="product-card__collection h5">
            {{ collection }}
          </component>

          <div v-if="hasTextLink" class="product-card__text-link">
            <div class="btn btn--link btn--icon">
              <span class="btn__text">{{ textLink }}</span>

              <span class="btn__icon">
                <phx-icon icon="chevron-10" bidirectional />
              </span>
            </div>
          </div>

          <component :is="headingTag" v-if="hasName" class="product-card__name h5">{{ name }}</component>

          <div v-if="hasSpecs" class="product-card__specs-wrapper">
            <div class="product-card__specs">
              <div v-if="hasSapReference" data-cy="product-card-reference">
                {{ $t('Product.reference', { reference: sapReference }) }}
              </div>
              <slot v-if="hasDescription" name="description">{{ description }}</slot>
            </div>
          </div>

          <div v-if="hasPrice" class="product-price product-card__price product-price--thin product-price--small">
            <product-price
              v-if="typeof price === 'object'"
              v-bind="localizedPrice(price)"
              :should-display-from-price="shouldDisplayFromPrice"
              class="product-price__value"
            />
            <span v-else class="product-price__value">{{ price }}</span>
            <span v-if="hasVatInformation" class="product-price__vat">{{ _vatInformation }}</span>
          </div>

          <slot name="extra-fees"></slot>

          <div
            v-for="error in errors"
            :key="error"
            class="alert alert--badge product-card__out-of-stock"
            data-cy="no-stock-alert-item"
          >
            <div class="alert__icon">
              <phx-icon icon="info-16" />
            </div>
            {{ $t(`Checkout.product.${error}.tag`) }}
          </div>
        </div>
      </component>

      <div v-if="has3D" class="product-card__3d">
        <div class="picture">
          <three-d-image />
        </div>
      </div>

      <watch-comparator-button
        v-if="shouldDisplayAddToWatchComparator"
        variant="checkbox"
        :reference="modelReference"
        :model-reference="modelReference"
        :name="name"
        :image="image?.image?.src || image?.src || ''"
        :link="url"
      />

      <div v-if="hasActions" class="product-card__actions">
        <wishlist-button
          v-if="shouldDisplayAddToWishlist"
          :reference="modelReference"
          :name="name"
          :tracking="tracking"
          :default-in-wishlist-icon="inWishlistIcon"
          :data-cy="dataCyWishlistButton"
        />

        <button
          v-if="shouldDisplayRemoveButton"
          type="button"
          class="btn btn--default btn--xs btn--icon-only btn--icon btn--transparent"
          :aria-label="$t('Checkout.removeProduct', { product: name })"
          :data-cy="dataCyRemoveButton"
          @click.stop="onRemove({ id, tracking })"
        >
          <span class="sr-only">$t('Checkout.removeProduct', { product: name })</span>

          <span class="btn__icon">
            <phx-icon icon="cross-16" />
          </span>
        </button>
      </div>

      <div v-if="hasVariants" class="product-card-variants">
        <div class="product-card-variants__title">{{ $t('ProductCard.variants') }}</div>

        <div class="product-card-variants__items">
          <div v-for="(variant, index) in variants" :key="index" class="picture product-card-variants__item">
            <picture-lazy v-bind="variant.image" />
          </div>

          <div v-if="hasVariantsIndicator" dir="ltr" class="product-card-variants__indicator"></div>
        </div>
      </div>

      <div v-if="ctaText" class="btn btn--default btn--full btn--negative product-card__quick-cta">
        <span class="btn__text">{{ ctaText }}</span>
      </div>
    </div>

    <div v-if="hasSliderImages" class="product-card__slider-nav-wrapper">
      <button
        ref="sliderPrevRef"
        type="button"
        class="btn btn--default btn--icon-only btn--icon btn btn--default product-card__slider-nav product-card__slider-nav--prev"
      >
        <span class="sr-only">{{ $t('Button.previousSlide') }}</span>
        <span class="btn__icon">
          <phx-icon icon="chevron-left-10" size="10" bidirectional />
        </span>
      </button>

      <button
        ref="sliderNextRef"
        type="button"
        class="btn btn--default btn--icon-only btn--icon btn btn--default product-card__slider-nav product-card__slider-nav--next"
      >
        <span class="sr-only">{{ $t('Button.nextSlide') }}</span>
        <span class="btn__icon">
          <phx-icon icon="chevron-10" size="10" bidirectional />
        </span>
      </button>
    </div>
  </div>
</template>
